import React from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { Gql } from '@fiverr-private/dynamic_matching';
import { formatDate } from '../../utils/formatDate';
import { BudgetAndTimelineItem } from './BudgetAndTimelineItem';

const T_PREFIX = 'brief_preview_drawer.person_brief.timeline';

const durationToKey = {
  [Gql.DmBriefHiringDuration.LessThanOneMonth]: 'less_than_one_month',
  [Gql.DmBriefHiringDuration.OneToThreeMonths]: 'one_to_three_months',
  [Gql.DmBriefHiringDuration.MoreThanThreeMonths]: 'more_than_three_months',
};

export const HiringDuration = ({ duration }: { duration: Gql.DmBriefHiringDuration }) => (
  <BudgetAndTimelineItem type="timeline" title="hiring_duration" iconName="duration">
    <I18n k={`${T_PREFIX}.hiring_duration.${durationToKey[duration]}`} />
  </BudgetAndTimelineItem>
);

const commitmentToKey = {
  [Gql.DmBriefTimeCommitment.OverThirtyHoursPerWeek]: 'more_than_thirty_hours',
  [Gql.DmBriefTimeCommitment.TenToThirtyHoursPerWeek]: 'between_ten_and_thirty_hours',
  [Gql.DmBriefTimeCommitment.UpToTenHoursPerWeek]: 'less_than_ten_hours',
};

export const TimeCommitment = ({ commitment }: { commitment: Gql.DmBriefTimeCommitment }) => (
  <BudgetAndTimelineItem type="timeline" title="time_commitment" iconName="commitment">
    <I18n k={`${T_PREFIX}.time_commitment.${commitmentToKey[commitment]}`} />
  </BudgetAndTimelineItem>
);

const deliveryTimelineToKey = {
  [Gql.DmBriefTimeline.SoonAsPossible]: 'as_soon_as_possible',
  [Gql.DmBriefTimeline.Flexible]: 'flexible',
  [Gql.DmBriefTimeline.SpecificDate]: 'specific_date',
  [Gql.DmBriefTimeline.GeneralTimeline]: 'general_timeline',
  [Gql.DmBriefTimeline.Ongoing]: 'ongoing',
};

const unitToKey = {
  [Gql.DmBriefTimeUnit.Day]: 'days',
  [Gql.DmBriefTimeUnit.Week]: 'weeks',
  [Gql.DmBriefTimeUnit.Month]: 'months',
};

interface DeliveryTimelineParams {
  date?: Gql.DmBrief['deliveryDate'];
  value?: number;
  unit?: string;
}

export const DeliveryTimeline = ({
  deliveryTimeline,
  specificDate,
  generalTimeline,
}: {
  deliveryTimeline: Gql.DmBriefTimeline;
  specificDate: Gql.DmBrief['deliveryDate'];
  generalTimeline?: Gql.DmBriefTimelineDetails['generalTimeline'];
}) => {
  const params: DeliveryTimelineParams = {};

  if (deliveryTimeline === Gql.DmBriefTimeline.SpecificDate) {
    params.date = formatDate(specificDate);
  }

  if (deliveryTimeline === Gql.DmBriefTimeline.GeneralTimeline && generalTimeline) {
    params.value = generalTimeline.value;
    params.unit = unitToKey[generalTimeline.unit];
  }

  return (
    <BudgetAndTimelineItem type="timeline" title="delivery_timeline" iconName="delivery">
      <I18n k={`${T_PREFIX}.delivery_timeline.${deliveryTimelineToKey[deliveryTimeline]}`} params={params} />
    </BudgetAndTimelineItem>
  );
};

export const Frequency = ({ frequency }: { frequency: Gql.DmBriefTimelineDetails['deliveriesFrequency'] }) => (
  <BudgetAndTimelineItem type="timeline" title="frequency" iconName="frequency">
    <I18n
      k={`${T_PREFIX}.frequency.${frequency?.value === 1 ? 'single_delivery' : 'deliveries'}`}
      params={{ value: frequency?.value, unit: String(frequency?.unit).toLocaleLowerCase() }}
    />
  </BudgetAndTimelineItem>
);
